import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Cpu, LineChart, Gem, Zap, MessageCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const WhyChooseUsSection = ({ whatsappUrl }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { language } = useLanguage();

  const content = {
    en: {
      overline: 'Why Sentetica',
      headline: "We Don't Just Market.",
      headlineAccent: 'We Transform.',
      subheadline: 'Our AI-powered approach ensures your brand reaches the right audience at the right time.',
      stats: [
        { value: '98%', label: 'Client Retention' },
        { value: '24h', label: 'Response Time' },
        { value: '5+', label: 'Years Experience' },
      ],
      cta: "Let's Talk Strategy",
      features: [
        { icon: Cpu, title: 'Data-Driven AI', desc: 'Cutting-edge algorithms for maximum impact.' },
        { icon: LineChart, title: 'Proven ROI', desc: 'Strategies that deliver 3x+ returns.' },
        { icon: Gem, title: 'Premium Quality', desc: 'Content that positions you as a leader.' },
        { icon: Zap, title: 'Full Automation', desc: 'Marketing funnel that works 24/7.' },
      ],
      badges: ['Meta Partner', 'Google Certified', 'AI Agency', 'Top Rated'],
    },
    tr: {
      overline: 'Neden Sentetica',
      headline: 'Sadece Pazarlamıyoruz.',
      headlineAccent: 'Dönüştürüyoruz.',
      subheadline: 'Yapay zeka destekli yaklaşımımız markanızın doğru zamanda doğru kitleye ulaşmasını sağlıyor.',
      stats: [
        { value: '98%', label: 'Müşteri Sadakati' },
        { value: '24s', label: 'Yanıt Süresi' },
        { value: '5+', label: 'Yıl Deneyim' },
      ],
      cta: 'Strateji Konuşalım',
      features: [
        { icon: Cpu, title: 'Veri Odaklı AI', desc: 'Maksimum etki için son teknoloji algoritmalar.' },
        { icon: LineChart, title: 'Kanıtlanmış ROI', desc: '3x+ getiri sağlayan stratejiler.' },
        { icon: Gem, title: 'Premium Kalite', desc: 'Sizi lider konumlandıran içerikler.' },
        { icon: Zap, title: 'Tam Otomasyon', desc: '7/24 çalışan pazarlama sistemi.' },
      ],
      badges: ['Meta Ortağı', 'Google Sertifikalı', 'AI Ajansı', 'En İyi'],
    },
  };

  const t = content[language] || content.en;

  return (
    <section id="why-us" className="py-16 md:py-20 bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold">{t.overline}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-3 tracking-tight leading-tight">
              {t.headline}<br />
              <span className="text-[#E60000]">{t.headlineAccent}</span>
            </h2>
            <p className="text-[#6B7280] text-base mt-4">{t.subheadline}</p>

            <div className="flex gap-8 mt-8">
              {t.stats.map((stat, i) => (
                <div key={i}>
                  <p className="font-heading text-3xl font-black text-[#E60000]">{stat.value}</p>
                  <p className="text-[#6B7280] text-sm">{stat.label}</p>
                </div>
              ))}
            </div>

            <motion.a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#E60000] text-white px-7 py-3.5 rounded-full font-bold mt-8 shadow-lg hover:bg-[#CC0000] hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="why-us-whatsapp-btn"
            >
              <MessageCircle size={18} />
              {t.cta}
              <ArrowRight size={16} />
            </motion.a>
          </motion.div>

          {/* Right */}
          <div className="grid grid-cols-2 gap-4">
            {t.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 15 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                className="bg-[#F9FAFB] rounded-xl p-5 border border-gray-100 hover:border-[#E60000]/20 hover:shadow-md transition-all duration-300 group"
                data-testid={`feature-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#E60000]/10 flex items-center justify-center mb-3 group-hover:bg-[#E60000] transition-colors duration-300">
                  <feature.icon className="w-5 h-5 text-[#E60000] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-1">{feature.title}</h3>
                <p className="text-[#6B7280] text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-12 pt-8 border-t border-gray-100"
        >
          <div className="flex flex-wrap justify-center items-center gap-6">
            {t.badges.map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-[#E60000]" />
                <span className="text-sm font-medium">{badge}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
